import { type RefObject, useCallback, useState } from 'react'
import type { PaginationDotsEdgeType } from '../types'

export const usePaginationDots = () => {
  const [translateX, setTranslateX] = useState(0)

  const scrollToCenter = useCallback(
    (containerRef: RefObject<HTMLDivElement>, elementIndex: number, isRTL: boolean) => {
      const container = containerRef.current
      if (!container) return
      const button = container.children[0].children[elementIndex] as HTMLButtonElement
      const containerWidth = container.offsetWidth // Visible area width
      const buttonWidth = button.offsetWidth // Width of the button

      // Button's position relative to the container
      let buttonOffset = button.offsetLeft
      if (isRTL) {
        buttonOffset = container.scrollWidth - button.offsetLeft - buttonWidth // Reverse the offset in RTL
      }

      const buttonCenter = buttonOffset + buttonWidth / 2 // Calculate the button's center relative to the container
      let newTranslateX = -(buttonCenter - containerWidth / 2) // Calculate the new translateX to center the button
      let maxTranslateX = 0 //  // Ensure translateX doesn't exceed bounds. It Cannot shift more than the start
      let minTranslateX = -(container.scrollWidth - containerWidth) // Cannot shift past the end

      // Adjust clamping for RTL
      if (isRTL) {
        newTranslateX = -newTranslateX; // Flip the direction for RTL
        [minTranslateX, maxTranslateX] = [-maxTranslateX, -minTranslateX] // Swap bounds
      }

      const clampedTranslateX = Math.max(minTranslateX, Math.min(maxTranslateX, newTranslateX))
      setTranslateX(clampedTranslateX)
    },
    [setTranslateX]
  )

  const validateIndex = (value: number, pages: number): number =>
    value < 0 || value >= pages ? 0 : value

  const getEdgePositions = (pages: number, index: number): PaginationDotsEdgeType => {
    const edges: PaginationDotsEdgeType = { first: undefined, last: undefined }
    if (pages <= 5) {
      return { first: undefined, last: undefined }
    }
    if (index === 0 || index === 1) {
      edges.last = 4
    } else if (index === pages - 1 || index === pages - 2) {
      edges.first = pages - 5
    } else {
      edges.first = index - 2
      edges.last = index + 2
    }
    return edges
  }

  return { getEdgePositions, scrollToCenter, translateX, validateIndex }
}
